import { useSetAtom } from "jotai";
import { useRef } from "react";

import { asPixels } from "@skilitics/designer-ui-components";
import {
  getElementOverlayGeometry,
  getHeightDelta,
  getWidthDelta,
  handleAffectsHeight,
  handleAffectsWidth,
  projectScreenDeltaToLocal,
  useDragDropManager,
  useDragDropMonitor,
  type ResizeHandle,
  type UniqueIdentifier,
} from "@skilitics/drag-drop";

import { useEventPublish } from "../../events/index.ts";
import { DND_CANVAS_EDITOR_ID, isResizeableId, parseResizeableId } from "../dnd/index.ts";
import { getNodeElement } from "../element-state/utils.ts";
import { propertiesEventTypes } from "../properties/propertiesEventTypes.ts";
import { canvasNodeResizeAtom } from "./canvasAtoms.ts";

const NODE_RESIZE_PREFIX = "node-";

export const parseNodeResizeHandle = (id: string): ResizeHandle | undefined => {
  if (!id.startsWith(NODE_RESIZE_PREFIX)) return undefined;
  return id.slice(NODE_RESIZE_PREFIX.length) as ResizeHandle;
};

export const isNodeResizeId = (identifier: UniqueIdentifier) => {
  if (!isResizeableId(identifier)) return false;
  const { id } = parseResizeableId(identifier);
  return id.startsWith(NODE_RESIZE_PREFIX);
};

/**
 * Monitors drag events for node resize handles.
 * During drag, updates an atom for real-time preview rendering.
 * On drag end, computes new dimensions from the source element's rect + delta,
 * then publishes a resize event with the final width/height values.
 *
 * Note: The transform from manager.dragOperation.transform is already snapped
 * by the SnapToEdges modifier in the modifier chain.
 */
export const useNodeResizeMonitor = () => {
  const publishEvent = useEventPublish();
  const setResizeState = useSetAtom(canvasNodeResizeAtom);
  const manager = useDragDropManager();
  // Skip identical setState calls during a snap-hold or sub-pixel pointer noise.
  // Without this, every pointer-move re-publishes the same delta to the atom,
  // which drives a useSyncExternalStore subscription and trips React's
  // "many updates inside startTransition" warning.
  const lastDelta = useRef<{ x: number; y: number } | null>(null);

  useDragDropMonitor({
    onDragMove(event) {
      const source = event.operation.source;
      if (!source || !isNodeResizeId(source.id)) return;

      const { id, layoutableId: nodeId } = parseResizeableId(source.id);
      const handle = parseNodeResizeHandle(id);
      if (!handle) return;

      // Read transform from the manager directly rather than from the event snapshot.
      // With feedback: "none" on the draggable, the Feedback plugin never reads the
      // transform getter, so the cached value in the snapshot stays at {x:0, y:0}.
      // Accessing manager.dragOperation.transform invokes the getter which computes
      // the modifier-adjusted (snap-adjusted) transform.
      const transform = manager?.dragOperation.transform;
      if (!transform) return;

      // Transform is in screen-space (no ScaleTransform modifier). Store as-is for
      // the resize preview (which renders in screen coords).
      const prev = lastDelta.current;
      if (prev && prev.x === transform.x && prev.y === transform.y) return;
      lastDelta.current = { x: transform.x, y: transform.y };
      setResizeState({ nodeId, handle, delta: { x: transform.x, y: transform.y } });
    },

    onDragEnd(event) {
      lastDelta.current = null;
      const source = event.operation.source;
      if (!source || !isNodeResizeId(source.id)) return;

      const { id, layoutableId: nodeId } = parseResizeableId(source.id);
      const handle = parseNodeResizeHandle(id);
      if (!handle) return;

      const transform = manager?.dragOperation.transform;

      setResizeState(undefined);

      if (!transform) return;

      // Find the actual node element (not the resize handle)
      const nodeElement = getNodeElement(nodeId);
      if (!nodeElement) return;

      const nodeGeometry = getElementOverlayGeometry(nodeElement, document.getElementById(DND_CANVAS_EDITOR_ID));
      // intrinsicRect.{width,height} come from element.offsetWidth/Height —
      // layout (CSS) pixels, unaffected by ancestor transforms. Using
      // intrinsicRect rather than the bounding-AABB keeps width/height
      // correct when the element is rotated or skewed.
      const currentWidth = nodeGeometry.intrinsicRect.width;
      const currentHeight = nodeGeometry.intrinsicRect.height;

      // Project the screen-space transform onto the element's local axes
      // (intrinsic, pre-transform pixels). geometry.scaleX/Y already include
      // the canvas zoom and any element-own scale, so no extra division.
      const localTransform = projectScreenDeltaToLocal(
        transform,
        nodeGeometry.rotation,
        nodeGeometry.scaleX,
        nodeGeometry.scaleY,
      );

      const result: {
        nodeId: number;
        handle: ResizeHandle;
        widthDelta?: number;
        heightDelta?: number;
        width?: string;
        height?: string;
      } = { nodeId, handle };

      if (handleAffectsWidth(handle)) {
        const widthDelta = getWidthDelta(handle, localTransform);
        const newWidth = Math.max(1, Math.round(currentWidth + widthDelta));
        result.width = asPixels(newWidth);
        result.widthDelta = Math.round(widthDelta);
      }

      if (handleAffectsHeight(handle)) {
        const heightDelta = getHeightDelta(handle, localTransform);
        const newHeight = Math.max(1, Math.round(currentHeight + heightDelta));
        result.height = asPixels(newHeight);
        result.heightDelta = Math.round(heightDelta);
      }

      publishEvent(propertiesEventTypes.RESIZE_NODE_DIMENSIONS, result);
    },
  });
};
